/**
 * Environment Library Service
 *
 * Persistence for Environment assets (reusable locations). Mirrors the
 * character studio service: localStorage-first cache with backend sync
 * (`GET/POST /environment/users/:userId/environments`, `DELETE .../:id`).
 */

import { API_BASE_URL } from '../config/api';
import { getAuthHeaders } from './utils/authTokenUtils';
import {
  Environment,
  EnvironmentIdentity,
  EnvironmentReferenceImage,
  MAX_ENVIRONMENT_IMAGE_LABEL_LENGTH,
  MAX_ENVIRONMENT_REFERENCE_IMAGES,
} from '../types/EnvironmentTypes';

const STORAGE_KEY = 'pixle_environments';
const MAX_ENVIRONMENT_IDENTITY_TAGS = 20;

function generateReferenceId(): string {
  return `envimg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function normalizeReferenceImage(
  entry: Partial<EnvironmentReferenceImage>,
  index: number
): EnvironmentReferenceImage | null {
  const url = (entry.url ?? '').trim();
  if (!url) return null;
  // User-editable label; never blank, capped at the shared max length.
  const rawName = (entry.name ?? '').trim();
  const name = (rawName || `Reference ${index + 1}`).slice(
    0,
    MAX_ENVIRONMENT_IMAGE_LABEL_LENGTH
  );
  return {
    id: entry.id?.trim() || generateReferenceId(),
    url,
    name,
    width:
      typeof entry.width === 'number' && Number.isFinite(entry.width)
        ? Math.round(entry.width)
        : 0,
    height:
      typeof entry.height === 'number' && Number.isFinite(entry.height)
        ? Math.round(entry.height)
        : 0,
  };
}

function normalizeIdentity(
  identity: EnvironmentIdentity | undefined,
  fallbackName: string,
  fallbackDescription: string
): EnvironmentIdentity {
  const raw = identity ?? {};
  const tags = (raw.tags ?? [])
    .filter((tag): tag is string => typeof tag === 'string')
    .map((tag) => tag.trim())
    .filter(Boolean)
    .slice(0, MAX_ENVIRONMENT_IDENTITY_TAGS);
  const normalized: EnvironmentIdentity = {};
  // Flat top-level fields stay canonical: an edit overwrites name/description
  // there, so they win over any stale nested copy carried on `identity`.
  const name = fallbackName || (raw.name ?? '').trim();
  const description = fallbackDescription || (raw.description ?? '').trim();
  if (name) normalized.name = name;
  if (description) normalized.description = description;
  if (tags.length > 0) normalized.tags = tags;
  return normalized;
}

function normalizeEnvironment(environment: Environment): Environment {
  const referenceImages = (environment.referenceImages ?? [])
    .map((entry, index) => normalizeReferenceImage(entry, index))
    .filter((entry): entry is EnvironmentReferenceImage => entry !== null)
    .slice(0, MAX_ENVIRONMENT_REFERENCE_IMAGES);
  const name = (environment.name ?? '').trim();
  const description = (environment.description ?? '').trim();

  return {
    ...environment,
    name,
    description,
    identity: normalizeIdentity(environment.identity, name, description),
    referenceImages,
  };
}

function getCurrentUserId(): string | null {
  try {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    return (
      storedUser?._id ||
      storedUser?.id ||
      localStorage.getItem('userId') ||
      null
    );
  } catch {
    return localStorage.getItem('userId');
  }
}

function normalizeEnvironmentList(data: unknown): Environment[] {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((environment) =>
    normalizeEnvironment(environment as Environment)
  );
}

function extractEnvironmentList(responseData: unknown): Environment[] {
  if (Array.isArray(responseData)) {
    return normalizeEnvironmentList(responseData);
  }

  if (
    responseData &&
    typeof responseData === 'object' &&
    'environments' in responseData
  ) {
    return normalizeEnvironmentList(
      (responseData as { environments?: unknown }).environments
    );
  }

  if (responseData && typeof responseData === 'object' && 'data' in responseData) {
    return normalizeEnvironmentList((responseData as { data?: unknown }).data);
  }

  return [];
}

function getEnvironmentApiUrl(userId: string): string {
  return `${API_BASE_URL}/environment/users/${userId}/environments`;
}

function persistLocalEnvironments(environments: Environment[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(environments));
}

/**
 * Fetch the current user's environments from the backend and hydrate the
 * local cache.
 */
export async function hydrateEnvironmentsForCurrentUser(): Promise<
  Environment[]
> {
  const userId = getCurrentUserId();
  if (!userId) {
    return getAllEnvironments();
  }

  try {
    const response = await fetch(getEnvironmentApiUrl(userId), {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch environment library data');
    }

    const responseData = await response.json();
    const environments = extractEnvironmentList(responseData);
    persistLocalEnvironments(environments);
    return environments;
  } catch (error) {
    console.error('Error hydrating environments:', error);
    return getAllEnvironments();
  }
}

/**
 * Generate a unique ID for a new environment.
 */
export function generateEnvironmentId(): string {
  return `env_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get all saved environments.
 */
export function getAllEnvironments(): Environment[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (data) {
      return JSON.parse(data).map(normalizeEnvironment);
    }
  } catch (error) {
    console.error('Error loading environments:', error);
  }
  return [];
}

/**
 * Get a single environment by ID.
 */
export function getEnvironmentById(id: string): Environment | null {
  const environments = getAllEnvironments();
  return environments.find((e) => e.id === id) || null;
}

/**
 * Save a new or existing environment (upsert), syncing to the backend.
 */
export async function saveEnvironment(
  environment: Environment
): Promise<Environment> {
  const normalized = normalizeEnvironment(environment);
  if (!normalized.name) {
    throw new Error('Environment name is required.');
  }
  if (normalized.referenceImages.length === 0) {
    throw new Error('At least one reference image is required.');
  }

  const environments = getAllEnvironments();
  const existingIndex = environments.findIndex((e) => e.id === normalized.id);

  if (existingIndex >= 0) {
    environments[existingIndex] = {
      ...normalized,
      updatedAt: new Date().toISOString(),
    };
  } else {
    environments.push({
      ...normalized,
      createdAt: normalized.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  }

  try {
    persistLocalEnvironments(environments);
  } catch (error) {
    console.error('Error saving environment:', error);
    throw new Error('Failed to save environment. Storage may be full.');
  }

  const userId = getCurrentUserId();
  if (!userId) {
    return normalized;
  }

  try {
    const response = await fetch(getEnvironmentApiUrl(userId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ environment: normalized }),
    });

    if (!response.ok) {
      throw new Error('Failed to persist environment');
    }

    await hydrateEnvironmentsForCurrentUser();
  } catch (error) {
    console.error('Error syncing environment to backend:', error);
  }

  return normalized;
}

/**
 * Delete an environment by ID.
 */
export async function deleteEnvironment(id: string): Promise<boolean> {
  const environments = getAllEnvironments();
  const filtered = environments.filter((e) => e.id !== id);

  if (filtered.length === environments.length) {
    return false; // Nothing was removed
  }

  try {
    persistLocalEnvironments(filtered);
    const userId = getCurrentUserId();
    if (userId) {
      const response = await fetch(`${getEnvironmentApiUrl(userId)}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        await hydrateEnvironmentsForCurrentUser();
      }
    }
    return true;
  } catch (error) {
    console.error('Error deleting environment:', error);
    return false;
  }
}

/**
 * Create a new environment record from uploaded reference images.
 */
export function createNewEnvironment(
  name: string,
  description: string,
  referenceImages: EnvironmentReferenceImage[]
): Environment {
  const now = new Date().toISOString();
  return normalizeEnvironment({
    id: generateEnvironmentId(),
    name,
    description,
    referenceImages,
    createdAt: now,
    updatedAt: now,
  });
}

/**
 * Build a reference-image entry for a freshly uploaded file.
 */
export function buildEnvironmentReferenceImage(
  location: string,
  fileName: string,
  width: number,
  height: number
): EnvironmentReferenceImage {
  return {
    id: generateReferenceId(),
    url: location,
    name: fileName,
    width,
    height,
  };
}
